import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, Lock, Mail, ShieldCheck } from "lucide-react";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import SEO from "@/components/site/SEO";
import BrandLogo from "@/components/site/BrandLogo";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-brand-ink text-white" data-testid="admin-login-page">
      <SEO title="Admin Sign In" path="/admin/login" />

      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-brand-ink">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-ink/95 via-brand-ink/85 to-brand-ink/95" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <BrandLogo variant="wordmark-dark" className="h-10 w-auto rounded-md" />
          </div>
          <div>
            <p className="label-gold">Operator console</p>
            <h1 className="mt-4 font-display font-black text-4xl text-balance">
              Manage every<br/>conversation. Securely.
            </h1>
            <p className="mt-5 text-white/65 max-w-md">
              View, review and action contact submissions from the OneDial Africa website
              and customer touchpoints — all in one place.
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/40">
            <ShieldCheck className="h-4 w-4 text-brand-green" />
            JWT · HttpOnly cookies · Brute-force protection
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-white text-brand-ink">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <BrandLogo variant="wordmark" className="h-10 w-auto" />
          </div>

          <p className="label-eyebrow">Admin Console</p>
          <h2 className="mt-3 font-display font-black text-3xl">Sign in to continue</h2>
          <p className="mt-2 text-sm text-brand-muted">
            Use your administrator credentials. Sessions expire automatically.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label className="block text-xs uppercase tracking-[0.22em] font-semibold mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
                <input
                  type="email"
                  data-testid="admin-input-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="admin@onedialafrica.com"
                  className="w-full rounded-md border border-brand-line bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.22em] font-semibold mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
                <input
                  type="password"
                  data-testid="admin-input-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full rounded-md border border-brand-line bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
                />
              </div>
            </div>

            {error && (
              <div data-testid="admin-login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-brand-green px-5 py-3 text-sm font-semibold text-white hover:bg-brand-greenHover disabled:opacity-70 transition-colors"
            >
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-xs text-brand-muted">
            Secured by Scitygate Telecom UK Ltd · Manchester, UK · Company No. 16468736
          </p>
        </div>
      </div>
    </div>
  );
}
